<template>
    <div class="webphone-contacts">
        <div class="contacts-header">
            <h4>Dahililer</h4>
            <button v-if="extensions.length > 0" @click="refreshContacts" class="refresh-btn">
                <i class="material-icons">refresh</i>
            </button>
        </div>

        <div v-if="extensions.length === 0" class="empty-state">
            <div class="empty-icon">
                <i class="material-icons">phone</i>
            </div>
            <div class="empty-text">Dahili listesi yüklenemedi</div>
            <button @click="refreshContacts" class="retry-btn">Yeniden Dene</button>
        </div>

        <div v-else class="contacts-list">
            <div class="search-box">
                <i class="material-icons search-icon">search</i>
                <input v-model="searchQuery" class="search-input" placeholder="Kişi ara..." />
                <button v-if="searchQuery" @click="searchQuery = ''" class="clear-search-btn">
                    <i class="material-icons">close</i>
                </button>
            </div>

            <div class="contact-sections">
                <!-- Recent Contacts -->
                <div v-if="recentContacts.length > 0" class="contact-section">
                    <div class="section-header">
                        <h5>Son Arananlar</h5>
                    </div>
                    <div
                        class="contact-item"
                        v-for="contact in recentContacts"
                        :key="'recent-' + contact.number"
                        :class="{ disabled: !canMakeCall(contact) }"
                        @click="handleContactClick(contact)"
                    >
                        <div class="contact-avatar-wrapper">
                            <AgentAvatar v-if="contact.userId" :userID="contact.userId" :size="40" :isNamed="false" />
                            <div
                                v-else
                                class="default-avatar"
                                :style="{ backgroundColor: getAvatarColor(contact.name) }"
                            >
                                <span>{{ getInitials(contact.name) }}</span>
                            </div>

                            <!-- Status indicator -->
                            <div class="status-indicator" :class="contact.status"></div>
                        </div>
                        <div class="contact-info">
                            <div class="contact-name">{{ contact.name }}</div>
                            <div class="contact-number">{{ contact.extension }}</div>
                            <div class="contact-status">
                                <i class="material-icons status-icon" :class="contact.status">
                                    {{ contact.status === "online" ? "phone" : "phone_disabled" }}
                                </i>
                                <span>{{ getStatusText(contact.status) }}</span>
                            </div>
                        </div>
                        <div class="contact-actions">
                            <button
                                class="action-btn call-btn"
                                :disabled="!canMakeCall(contact)"
                                @click.stop="handleCall(contact)"
                            >
                                <i class="material-icons">call</i>
                            </button>
                        </div>
                    </div>
                </div>

                <!-- All Contacts -->
                <div class="contact-section">
                    <div class="section-header">
                        <h5>Tüm Kişiler</h5>
                        <span class="contact-count">{{ filteredContacts.length }}</span>
                    </div>

                    <div v-if="filteredContacts.length === 0" class="no-results">
                        <i class="material-icons">search_off</i>
                        <span>Arama sonucu bulunamadı</span>
                    </div>

                    <div v-else class="contacts-grid">
                        <div
                            class="contact-item"
                            v-for="contact in filteredContacts"
                            :key="contact.number"
                            :class="{ disabled: !canMakeCall(contact) }"
                            @click="handleContactClick(contact)"
                        >
                            <!-- Avatar - eğer user mapping varsa AgentAvatar kullan -->
                            <div class="contact-avatar-wrapper">
                                <AgentAvatar
                                    v-if="contact.userId"
                                    :userID="contact.userId"
                                    :size="40"
                                    :isNamed="false"
                                />
                                <div
                                    v-else
                                    class="default-avatar"
                                    :style="{ backgroundColor: getAvatarColor(contact.name) }"
                                >
                                    <span>{{ getInitials(contact.name) }}</span>
                                </div>

                                <!-- Status indicator -->
                                <div class="status-indicator" :class="contact.status"></div>
                            </div>

                            <div class="contact-info">
                                <div class="contact-name">{{ contact.name }}</div>
                                <div class="contact-number">{{ contact.extension }}</div>
                                <div class="contact-status">
                                    <i class="material-icons status-icon" :class="contact.status">
                                        {{ contact.status === "online" ? "phone" : "phone_disabled" }}
                                    </i>
                                    <span>{{ getStatusText(contact.status) }}</span>
                                </div>
                            </div>

                            <div class="contact-actions">
                                <button class="action-btn info-btn" @click.stop="showContactInfo(contact)">
                                    <i class="material-icons">info</i>
                                </button>
                                <button
                                    class="action-btn call-btn"
                                    :disabled="!canMakeCall(contact)"
                                    @click.stop="handleCall(contact)"
                                >
                                    <i class="material-icons">call</i>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Contact Info Modal -->
        <div v-if="selectedContact" class="contact-info-modal" @click.self="selectedContact = null">
            <div class="modal-content">
                <div class="modal-header">
                    <div
                        class="contact-avatar-large"
                        :style="{ backgroundColor: getAvatarColor(selectedContact.name) }"
                    >
                        <span>{{ getInitials(selectedContact.name) }}</span>
                    </div>
                    <div class="contact-title">
                        <h4>{{ selectedContact.name }}</h4>
                        <p>{{ selectedContact.number }}</p>
                    </div>
                    <button @click="selectedContact = null" class="close-btn">
                        <i class="material-icons">close</i>
                    </button>
                </div>

                <div class="modal-body">
                    <div class="info-row">
                        <span class="label">Dahili Numarası:</span>
                        <span class="value">{{ selectedContact.extension }}</span>
                    </div>

                    <div v-if="selectedContact.email" class="info-row">
                        <span class="label">E-posta:</span>
                        <span class="value">{{ selectedContact.email }}</span>
                    </div>

                    <div v-if="selectedContact.department" class="info-row">
                        <span class="label">Departman:</span>
                        <span class="value">{{ selectedContact.department }}</span>
                    </div>

                    <div v-if="selectedContact.title" class="info-row">
                        <span class="label">Ünvan:</span>
                        <span class="value">{{ selectedContact.title }}</span>
                    </div>

                    <div class="info-row">
                        <span class="label">Durum:</span>
                        <span class="value status" :class="selectedContact.status">
                            {{ getStatusText(selectedContact.status) }}
                        </span>
                    </div>
                </div>

                <div class="modal-actions">
                    <button @click="selectedContact = null" class="cancel-btn">Kapat</button>
                    <button @click="callFromModal" class="call-btn">
                        <i class="material-icons">call</i>
                        Ara
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import Vue from "vue";
import AgentAvatar from "../../../../../components/common/AgentAvatar.vue";

export default Vue.extend({
    name: "WebPhoneContacts",
    components: {
        AgentAvatar,
    },
    props: {
        extensions: {
            type: Array,
            default: () => [],
        },
        mapping: {
            type: Object,
            default: () => ({}),
        },
        callState: {
            type: Object,
            default: () => ({ status: "idle" }),
        },
    },
    data() {
        return {
            searchQuery: "",
            selectedContact: null,
            recentNumbers: [], // Numbers from call history
        };
    },
    computed: {
        processedExtensions() {
            // Dahilileri contact formatına çevir
            return this.extensions.map((ext) => {
                const userId = this.mapping[ext.extension];
                const isOnline = !!ext.registeraddr;

                return {
                    number: ext.extension,
                    name: ext.displayname,
                    extension: ext.extension,
                    status: isOnline ? "online" : "offline",
                    userId: userId,
                    registeraddr: ext.registeraddr,
                    device: ext.device,
                    expiration: ext.expiration,
                };
            });
        },

        filteredContacts() {
            if (!this.searchQuery) {
                return this.processedExtensions;
            }

            const query = this.searchQuery.toLowerCase();
            return this.processedExtensions.filter(
                (contact) =>
                    contact.name.toLowerCase().includes(query) ||
                    contact.number.toLowerCase().includes(query) ||
                    contact.extension.toLowerCase().includes(query)
            );
        },

        recentContacts() {
            // Get extensions that were recently called
            return this.processedExtensions
                .filter((contact) => this.recentNumbers.includes(contact.number))
                .slice(0, 3);
        },
    },
    methods: {
        getInitials(name) {
            if (!name) return "?";

            const parts = name.trim().split(" ");
            if (parts.length === 1) {
                return parts[0].charAt(0).toUpperCase();
            } else {
                return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
            }
        },

        getAvatarColor(name) {
            const colors = [
                "#FF6B6B",
                "#4ECDC4",
                "#45B7D1",
                "#96CEB4",
                "#FECA57",
                "#FF9FF3",
                "#54A0FF",
                "#5F27CD",
                "#00D2D3",
                "#FF9F43",
                "#10AC84",
                "#EE5A24",
                "#0984E3",
                "#B71C1C",
                "#6C5CE7",
            ];

            if (!name) return colors[0];

            let hash = 0;
            for (let i = 0; i < name.length; i++) {
                hash = name.charCodeAt(i) + ((hash << 5) - hash);
            }

            return colors[Math.abs(hash) % colors.length];
        },

        getStatusText(status) {
            console.log("getStatusText",status)
            switch (status) {
                case "online":
                    return "Kayıtlı";
                case "offline":
                    return "Kayıtsız";
                default:
                    return "Bilinmiyor";
            }
        },

        canMakeCall(contact) {
            // Sadece idle modda ve dahili online ise arama yapılabilir
            return this.callState.status === "idle" && contact.status === "online";
        },

        handleContactClick(contact) {
            if (this.canMakeCall(contact)) {
                this.handleCall(contact);
            } else {
                this.showCallWarning(contact);
            }
        },

        handleCall(contact) {
            if (!this.canMakeCall(contact)) {
                this.showCallWarning(contact);
                return;
            }

            this.$emit("call", contact);
        },

        showCallWarning(contact) {
            if (this.callState.status !== "idle") {
                this.$message.warning("Şu anda başka bir arama devam ediyor.");
            } else if (contact.status !== "online") {
                this.$message.warning(`${contact.name} dahili kayıtlı değil, arama yapılamaz.`);
            }
        },

        showContactInfo(contact) {
            this.selectedContact = contact;
        },

        callFromModal() {
            if (this.selectedContact) {
                this.$emit("call", this.selectedContact);
                this.selectedContact = null;
            }
        },

        refreshContacts() {
            this.$emit("refresh-contacts");
        },

        updateRecentNumbers(callHistory) {
            // Extract recent unique numbers from call history
            const recentCalls = callHistory.slice(0, 10);
            this.recentNumbers = [...new Set(recentCalls.map((call) => call.number))];
        },
    },
    mounted() {
        // Load recent contacts from localStorage if available
        try {
            const saved = localStorage.getItem("webphone_recent_contacts");
            if (saved) {
                this.recentNumbers = JSON.parse(saved);
            }
        } catch (error) {
            console.error("Failed to load recent contacts:", error);
        }
    },
    watch: {
        recentNumbers: {
            handler(newNumbers) {
                // Save to localStorage
                try {
                    localStorage.setItem("webphone_recent_contacts", JSON.stringify(newNumbers));
                } catch (error) {
                    console.error("Failed to save recent contacts:", error);
                }
            },
            deep: true,
        },
    },
});
</script>

<style scoped lang="scss">
.webphone-contacts {
    height: 100%;
    display: flex;
    flex-direction: column;
    padding: 20px;
}

.contacts-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;

    h4 {
        margin: 0;
        font-size: 16px;
        font-weight: 600;
        color: #1c1c1e;
    }

    .refresh-btn {
        background: none;
        border: none;
        padding: 8px;
        border-radius: 8px;
        cursor: pointer;
        color: #007aff;

        &:hover {
            background: rgba(0, 122, 255, 0.1);
        }

        .material-icons {
            font-size: 20px;
        }
    }
}

.empty-state {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    color: #8e8e93;
    text-align: center;

    .empty-icon {
        margin-bottom: 12px;

        .material-icons {
            font-size: 48px;
            color: #c7c7cc;
        }
    }

    .empty-text {
        font-size: 14px;
        margin-bottom: 16px;
    }

    .retry-btn {
        background: #007aff;
        color: white;
        border: none;
        padding: 8px 16px;
        border-radius: 8px;
        cursor: pointer;
        font-size: 14px;

        &:hover {
            background: #0056cc;
        }
    }
}

.contacts-list {
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow: hidden;

    .search-box {
        position: relative;
        margin-bottom: 20px;

        .search-icon {
            position: absolute;
            left: 12px;
            top: 50%;
            transform: translateY(-50%);
            color: #8e8e93;
            font-size: 18px;
        }

        .search-input {
            width: 100%;
            padding: 10px 40px 10px 40px;
            border: 1px solid #d1d1d6;
            border-radius: 8px;
            font-size: 14px;
            background: white;

            &:focus {
                outline: none;
                border-color: #007aff;
            }

            &::placeholder {
                color: #8e8e93;
            }
        }

        .clear-search-btn {
            position: absolute;
            right: 8px;
            top: 50%;
            transform: translateY(-50%);
            background: none;
            border: none;
            padding: 4px;
            border-radius: 4px;
            cursor: pointer;
            color: #8e8e93;

            &:hover {
                background: rgba(0, 0, 0, 0.05);
            }

            .material-icons {
                font-size: 16px;
            }
        }
    }
}

.contact-sections {
    flex: 1;
    overflow-y: auto;

    .contact-section {
        margin-bottom: 24px;

        &:last-child {
            margin-bottom: 0;
        }

        .section-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 12px;

            h5 {
                margin: 0;
                font-size: 14px;
                font-weight: 600;
                color: #1c1c1e;
                text-transform: uppercase;
                letter-spacing: 0.5px;
            }

            .contact-count {
                font-size: 12px;
                color: #8e8e93;
                background: #f2f2f7;
                padding: 2px 8px;
                border-radius: 12px;
            }
        }

        .no-results {
            display: flex;
            flex-direction: column;
            align-items: center;
            padding: 40px 20px;
            color: #8e8e93;

            .material-icons {
                font-size: 32px;
                margin-bottom: 8px;
                color: #c7c7cc;
            }

            span {
                font-size: 14px;
            }
        }
    }
}

.contacts-grid {
    display: flex;
    flex-direction: column;
    gap: 1px;

    .contact-item {
        display: flex;
        align-items: center;
        padding: 12px 0;
        border-bottom: 1px solid #f2f2f7;
        cursor: pointer;
        transition: background-color 0.2s ease;

        &:hover {
            background: rgba(0, 0, 0, 0.03);
        }

        &:last-child {
            border-bottom: none;
        }

        .contact-avatar-wrapper {
            flex: 0 0 auto;
            position: relative;
            margin-right: 12px;

            .default-avatar {
                width: 40px;
                height: 40px;
                border-radius: 50%;
                display: flex;
                align-items: center;
                justify-content: center;
                font-weight: 600;
                color: white;
                font-size: 14px;
            }

            .status-indicator {
                position: absolute;
                bottom: 0;
                right: 0;
                width: 12px;
                height: 12px;
                border-radius: 50%;
                border: 2px solid white;

                &.online {
                    background: #34c759;
                }

                &.offline {
                    background: #ff9500;
                }
            }
        }

        .contact-info {
            flex: 1;
            min-width: 0;

            .contact-name {
                font-size: 15px;
                font-weight: 500;
                color: #1c1c1e;
                margin-bottom: 2px;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
            }

            .contact-number {
                font-size: 13px;
                color: #8e8e93;
                margin-bottom: 1px;
            }

            .contact-status {
                display: flex;
                align-items: center;
                gap: 4px;
                font-size: 11px;
                color: #8e8e93;

                .status-icon {
                    font-size: 12px;

                    &.online {
                        color: #34c759;
                    }

                    &.offline {
                        color: #ff9500;
                    }
                }

                span {
                    font-size: 11px;
                }
            }
        }

        .contact-actions {
            flex: 0 0 auto;
            display: flex;
            gap: 8px;
            opacity: 0;
            transition: opacity 0.2s ease;

            .action-btn {
                width: 32px;
                height: 32px;
                border-radius: 50%;
                border: none;
                display: flex;
                align-items: center;
                justify-content: center;
                cursor: pointer;
                transition: all 0.2s ease;

                .material-icons {
                    font-size: 16px;
                    color: white;
                }

                &.info-btn {
                    background: #8e8e93;

                    &:hover {
                        background: #6d6d70;
                    }
                }

                &.call-btn {
                    background: #34c759;

                    &:hover {
                        background: #2fb653;
                    }
                }
            }
        }

        &:hover .contact-actions {
            opacity: 1;
        }

        &.disabled {
            opacity: 0.6;
            cursor: not-allowed;

            .contact-actions .call-btn:disabled {
                background: #c7c7cc !important;
                cursor: not-allowed;

                &:hover {
                    background: #c7c7cc !important;
                }
            }
        }
    }
}

.contact-info-modal {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10000;

    .modal-content {
        background: white;
        border-radius: 12px;
        width: 90%;
        max-width: 400px;
        max-height: 80vh;
        overflow: hidden;

        .modal-header {
            display: flex;
            align-items: center;
            padding: 20px;
            border-bottom: 1px solid #f2f2f7;

            .contact-avatar-large {
                width: 50px;
                height: 50px;
                border-radius: 50%;
                display: flex;
                align-items: center;
                justify-content: center;
                margin-right: 15px;
                font-weight: 600;
                color: white;
                font-size: 18px;
            }

            .contact-title {
                flex: 1;

                h4 {
                    margin: 0 0 4px 0;
                    font-size: 16px;
                    font-weight: 600;
                    color: #1c1c1e;
                }

                p {
                    margin: 0;
                    font-size: 14px;
                    color: #8e8e93;
                }
            }

            .close-btn {
                background: none;
                border: none;
                padding: 4px;
                border-radius: 4px;
                cursor: pointer;
                color: #8e8e93;

                &:hover {
                    background: rgba(0, 0, 0, 0.05);
                }

                .material-icons {
                    font-size: 20px;
                }
            }
        }

        .modal-body {
            padding: 20px;

            .info-row {
                display: flex;
                justify-content: space-between;
                align-items: center;
                padding: 8px 0;
                border-bottom: 1px solid #f2f2f7;

                &:last-child {
                    border-bottom: none;
                }

                .label {
                    font-size: 14px;
                    color: #8e8e93;
                    font-weight: 500;
                }

                .value {
                    font-size: 14px;
                    color: #1c1c1e;
                    font-weight: 500;
                    text-align: right;

                    &.status {
                        &.online {
                            color: #34c759;
                        }

                        &.busy {
                            color: #ff9500;
                        }

                        &.away {
                            color: #ff9500;
                        }

                        &.offline {
                            color: #8e8e93;
                        }
                    }
                }
            }
        }

        .modal-actions {
            display: flex;
            gap: 12px;
            padding: 20px;
            border-top: 1px solid #f2f2f7;

            button {
                flex: 1;
                padding: 12px;
                border: none;
                border-radius: 8px;
                font-weight: 600;
                cursor: pointer;
                display: flex;
                align-items: center;
                justify-content: center;
                gap: 6px;

                &.cancel-btn {
                    background: #f2f2f7;
                    color: #1c1c1e;

                    &:hover {
                        background: #e5e5ea;
                    }
                }

                &.call-btn {
                    background: #007aff;
                    color: white;

                    &:hover {
                        background: #0056cc;
                    }

                    .material-icons {
                        font-size: 18px;
                    }
                }
            }
        }
    }
}

// Scrollbar styling
.contact-sections {
    &::-webkit-scrollbar {
        width: 6px;
    }

    &::-webkit-scrollbar-track {
        background: transparent;
    }

    &::-webkit-scrollbar-thumb {
        background: #c7c7cc;
        border-radius: 3px;

        &:hover {
            background: #a1a1a6;
        }
    }
}

// Dark mode support
@media (prefers-color-scheme: dark) {
    .webphone-contacts {
        .contacts-header h4 {
            color: white;
        }

        .contacts-list .search-box .search-input {
            background: #2c2c2e;
            border-color: #3a3a3c;
            color: white;

            &::placeholder {
                color: #8e8e93;
            }
        }

        .contact-sections .contact-section {
            .section-header {
                h5 {
                    color: white;
                }

                .contact-count {
                    background: #2c2c2e;
                    color: #8e8e93;
                }
            }

            .contacts-grid .contact-item {
                border-bottom-color: #2c2c2e;

                &:hover {
                    background: rgba(255, 255, 255, 0.05);
                }

                .contact-info .contact-name {
                    color: white;
                }
            }
        }

        .contact-info-modal .modal-content {
            background: #1c1c1e;

            .modal-header {
                border-bottom-color: #2c2c2e;

                .contact-title h4 {
                    color: white;
                }
            }

            .modal-body .info-row {
                border-bottom-color: #2c2c2e;

                .value {
                    color: white;
                }
            }

            .modal-actions {
                border-top-color: #2c2c2e;

                .cancel-btn {
                    background: #2c2c2e;
                    color: white;

                    &:hover {
                        background: #3a3a3c;
                    }
                }
            }
        }
    }
}
</style>
